import esriRequest from 'esri/request';

import { Promise as bbPromise } from 'promise';

import { criteriaStore } from 'stores/CriteriaStore';
import { reportStore } from 'stores/ReportStore';

import standardsConfig from 'js/standards.config';

import { romanize } from 'utils/Utilities';

const handle710Results = (standardsVersion, load, riskLevelValue, results) => {
  const usgsVal = results.response.data;
  load.sampleData.value = ['Risk Category ' + results.request.parameters.riskCategory];
  load.details = [
    {
      type: 'singleLine',
      label: 'Risk Category ' + results.request.parameters.riskCategory,
      value: '',
    },
    {
      type: 'grid',
      label: '',
      items: [
        {
          label: 'SS',
          value: usgsVal.ss,
        },
        {
          label: 'S1',
          value: usgsVal.s1,
        },
        {
          label: 'FA',
          value: usgsVal.fa,
        },
        {
          label: 'FV',
          value: usgsVal.fv,
        },
        {
          label: 'SMS',
          value: usgsVal.sms,
        },
        {
          label: 'SM1',
          value: usgsVal.sm1,
        },
        {
          label: 'SDS',
          value: usgsVal.sds,
        },
        {
          label: 'SD1',
          value: usgsVal.sd1,
        },
        {
          label: 'TL',
          value: usgsVal['t-sub-l'],
        },
        {
          label: 'PGA',
          value: usgsVal.pga,
        },
        {
          label: 'PGAM',
          value: usgsVal.pgam,
        },
        {
          label: 'FPGA',
          value: usgsVal.fpga,
        },
        {
          label: 'LE',
          value: riskLevelValue,
        },
        {
          label: 'CV',
          value: usgsVal.cv,
        },
      ],
    },
  ];

  load.details[1].items = load.details[1].items.filter((v) => {
    return !isNaN(v.value);
  });

  // This coefficient must be different than the rest...
  if (results.response.data.hasOwnProperty('sdc') && results.response.data.sdc !== null) {
    load.details.push({
      type: 'singleLine',
      label: 'Seismic Design Category',
      value: results.response.data.sdc,
    });
  }

  // SD Spectrum
  const twoPeriodDesignSpectrumChartData = {};
  twoPeriodDesignSpectrumChartData.type = 'chart';
  twoPeriodDesignSpectrumChartData.title = 'Design Response Spectrum';
  twoPeriodDesignSpectrumChartData.labels = results.response.data.twoPeriodDesignSpectrum.map((point) => point[0]);
  twoPeriodDesignSpectrumChartData.data = results.response.data.twoPeriodDesignSpectrum.map((point) => ({
    x: point[0],
    y: point[1],
  }));

  load.details.push(twoPeriodDesignSpectrumChartData);
  // SM Spectrum
  const twoPeriodMCErSpectrumChartData = {};
  twoPeriodMCErSpectrumChartData.type = 'chart';
  twoPeriodMCErSpectrumChartData.title = 'MCER Response Spectrum';
  twoPeriodMCErSpectrumChartData.labels = results.response.data.twoPeriodMCErSpectrum.map((point) => point[0]);
  twoPeriodMCErSpectrumChartData.data = results.response.data.twoPeriodMCErSpectrum.map((point) => ({
    x: point[0],
    y: point[1],
  }));
  load.details.push(twoPeriodMCErSpectrumChartData);

  load.details.push({
    type: 'dataSource',
    dataSource: {
      text: standardsConfig[standardsVersion].dataSource,
    },
  });
  reportStore.emitChange();
};

const handle716Results = (standardsVersion, load, riskLevelValue, results) => {
  Object.keys(results.response.data).forEach((key) => {
    // console.log(result.response.data[key], key)
    if (
      results.response.data[key] === '' ||
      results.response.data[key] === null ||
      results.response.data[key] === undefined
    ) {
      // console.log('UPDATED!', results.response.data[key])
      results.response.data[key] = 'N/A';
    }
  });
  load.sampleData.value = ['Risk Category ' + results.request.parameters.riskCategory];
  load.details = [
    {
      type: 'singleLine',
      label: 'Risk Category ' + results.request.parameters.riskCategory,
      value: '',
    },
    {
      type: 'grid',
      label: '',
      items: [
        {
          label: 'SS',
          value: results.response.data.ss,
        },
        {
          label: 'S1',
          value: results.response.data.s1,
        },
        {
          label: 'Fa',
          value: results.response.data.fa,
        },
        {
          label: 'Fv',
          value: results.response.data.fv,
        },
        {
          label: 'SMS',
          value: results.response.data.sms,
        },
        {
          label: 'SM1',
          value: results.response.data.sm1,
        },
        {
          label: 'SDS',
          value: results.response.data.sds,
        },
        {
          label: 'SD1',
          value: results.response.data.sd1,
        },
        {
          label: 'TL',
          value: results.response.data['t-sub-l'],
        },
        {
          label: 'PGA',
          value: results.response.data.pga,
        },
        {
          label: 'PGAM',
          value: results.response.data.pgam,
        },
        {
          label: 'FPGA',
          value: results.response.data.fpga,
        },
        {
          label: 'le',
          value: riskLevelValue,
        },
        {
          label: 'Cv',
          value: results.response.data.cv,
        },
      ],
    },
  ];

  if (typeof results.response.data.fa === 'number' && typeof results.response.data.fv === 'number') {
    // This coefficient must be different than the rest...
    if (results.response.data.hasOwnProperty('sdc') && results.response.data.sdc !== null) {
      load.details.push({
        type: 'singleLine',
        label: 'Seismic Design Category',
        value: results.response.data.sdc,
      });
    }

    // SD Spectrum
    const twoPeriodDesignSpectrumChartData = {};
    twoPeriodDesignSpectrumChartData.type = 'chart';
    twoPeriodDesignSpectrumChartData.title = 'Design Response Spectrum';
    twoPeriodDesignSpectrumChartData.labels = results.response.data.twoPeriodDesignSpectrum.map((point) => point[0]);
    twoPeriodDesignSpectrumChartData.data = results.response.data.twoPeriodDesignSpectrum.map((point) => ({
      x: point[0],
      y: point[1],
    }));

    load.details.push(twoPeriodDesignSpectrumChartData);
    // SM Spectrum
    const twoPeriodMCErSpectrumChartData = {};
    twoPeriodMCErSpectrumChartData.type = 'chart';
    twoPeriodMCErSpectrumChartData.title = 'MCER Response Spectrum';
    twoPeriodMCErSpectrumChartData.labels = results.response.data.twoPeriodMCErSpectrum.map((point) => point[0]);
    twoPeriodMCErSpectrumChartData.data = results.response.data.twoPeriodMCErSpectrum.map((point) => ({
      x: point[0],
      y: point[1],
    }));
    load.details.push(twoPeriodMCErSpectrumChartData);
    // SAM Spectrum
    const verticalDesignSpectrumChartData = {};
    verticalDesignSpectrumChartData.type = 'chart';
    verticalDesignSpectrumChartData.title = 'Design Vertical Response Spectrum';
    verticalDesignSpectrumChartData.labels = results.response.data.verticalDesignSpectrum.map((point) => point[0]);
    verticalDesignSpectrumChartData.data = results.response.data.verticalDesignSpectrum.map((point) => ({
      x: point[0],
      y: point[1],
    }));
    load.details.push(verticalDesignSpectrumChartData);
    // SAMV Spectrum
    const savmSpectrumChartData = {};
    savmSpectrumChartData.type = 'chart';
    savmSpectrumChartData.title = 'MCER Vertical Response Spectrum';
    savmSpectrumChartData.labels = results.response.data.verticalMCErSpectrum.map((point) => point[0]);
    savmSpectrumChartData.data = results.response.data.verticalMCErSpectrum.map((point) => ({
      x: point[0],
      y: point[1],
    }));
    load.details.push(savmSpectrumChartData);
  } else {
    // if fa, fv or both is null:
    // check for null spectrum
    if (
      results.response.data.twoPeriodDesignSpectrum === 'N/A' ||
      results.response.data.twoPeriodMCErSpectrum === 'N/A'
    ) {
      load.details.push({
        type: 'singleLine',
        jsxLabel: 'NO SEISMIC SPECTRUM',
        value: 'Case Study Required',
      });
    }

    // instead of graphs push this message
    load.details.push({
      type: 'singleLine',
      label: 'Note',
      value: 'Ground motion hazard analysis may be required. See ASCE/SEI 7-16 Section 11.4.8.',
    });
  }
  load.details.push({
    type: 'dataSource',
    dataSource: {
      text: standardsConfig[standardsVersion].dataSource,
    },
  });
  reportStore.emitChange();
};

const handle722Results = (standardsVersion, load, results) => {
  Object.keys(results.response.data).forEach((key) => {
    // console.log(result.response.data[key], key)
    if (
      results.response.data[key] === '' ||
      results.response.data[key] === null ||
      results.response.data[key] === undefined
    ) {
      // console.log('UPDATED!', results.response.data[key])
      results.response.data[key] = 'N/A';
    }
  });
  load.sampleData.value = ['Risk Category ' + results.request.parameters.riskCategory];
  load.details = [
    {
      type: 'singleLine',
      label: 'Risk Category ' + results.request.parameters.riskCategory,
      value: '',
    },
    {
      type: 'grid',
      label: '',
      items: [
        {
          label: 'SS',
          value: results.response.data.ss,
        },
        {
          label: 'S1',
          value: results.response.data.s1,
        },
        {
          label: 'SMS',
          value: results.response.data.sms,
        },
        {
          label: 'SM1',
          value: results.response.data.sm1,
        },
        {
          label: 'SDS',
          value: results.response.data.sds,
        },
        {
          label: 'SD1',
          value: results.response.data.sd1,
        },
        {
          label: 'TL',
          value: results.response.data.tl,
        },
        {
          label: 'PGAM',
          value: results.response.data.pgam,
        },
        {
          label: 'VS30',
          value: results.response.metadata.vs30,
        },
      ],
    },
  ];

  if (results.response.data.hasOwnProperty('sdc') && results.response.data.sdc !== null) {
    load.details.push({
      type: 'singleLine',
      label: 'Seismic Design Category',
      value: results.response.data.sdc,
    });
  }

  // Multi-Period Design Spectrum
  const multiPeriodDesignSpectrumChartData = {};
  multiPeriodDesignSpectrumChartData.type = 'chart';
  multiPeriodDesignSpectrumChartData.title = 'Multi-Period Design Spectrum';
  multiPeriodDesignSpectrumChartData.labels = results.response.data.multiPeriodDesignSpectrum.periods;
  multiPeriodDesignSpectrumChartData.data = results.response.data.multiPeriodDesignSpectrum.ordinates.map(
    (point, index) => ({ x: results.response.data.multiPeriodDesignSpectrum.periods[index], y: point })
  );
  load.details.push(multiPeriodDesignSpectrumChartData);

  // Multi-Period Spectrum
  const multiPeriodMCErSpectrumChartData = {};
  multiPeriodMCErSpectrumChartData.type = 'chart';
  multiPeriodMCErSpectrumChartData.title = 'Multi-Period MCER Spectrum';
  multiPeriodMCErSpectrumChartData.labels = results.response.data.multiPeriodMCErSpectrum.periods;
  multiPeriodMCErSpectrumChartData.data = results.response.data.multiPeriodMCErSpectrum.ordinates.map(
    (point, index) => ({ x: results.response.data.multiPeriodMCErSpectrum.periods[index], y: point })
  );
  load.details.push(multiPeriodMCErSpectrumChartData);

  // SD Spectrum
  const twoPeriodDesignSpectrumChartData = {};
  twoPeriodDesignSpectrumChartData.type = 'chart';
  twoPeriodDesignSpectrumChartData.title = 'Two-Period Design Spectrum';
  twoPeriodDesignSpectrumChartData.labels = results.response.data.twoPeriodDesignSpectrum.periods;
  twoPeriodDesignSpectrumChartData.data = results.response.data.twoPeriodDesignSpectrum.ordinates.map(
    (point, index) => ({ x: results.response.data.twoPeriodDesignSpectrum.periods[index], y: point })
  );

  load.details.push(twoPeriodDesignSpectrumChartData);

  // SM Spectrum
  const twoPeriodMCErSpectrumChartData = {};
  twoPeriodMCErSpectrumChartData.type = 'chart';
  twoPeriodMCErSpectrumChartData.title = 'Two-Period MCER Spectrum';
  twoPeriodMCErSpectrumChartData.labels = results.response.data.twoPeriodMCErSpectrum.periods;
  twoPeriodMCErSpectrumChartData.data = results.response.data.twoPeriodMCErSpectrum.ordinates.map((point, index) => ({
    x: results.response.data.twoPeriodMCErSpectrum.periods[index],
    y: point,
  }));
  load.details.push(twoPeriodMCErSpectrumChartData);

  load.details.push({
    type: 'singleLine',
    label: 'Note',
    value:
      'Where values of the multi-period 5%-damped MCER response spectrum are not available from the USGS Seismic Design Geodatabase, the design response spectrum shall be permitted to be determined in accordance with Section 11.4.5.2',
  });

  // SAM Spectrum
  const verticalDesignSpectrumChartData = {};
  verticalDesignSpectrumChartData.type = 'chart';
  verticalDesignSpectrumChartData.title = 'Design Vertical Response Spectrum';
  verticalDesignSpectrumChartData.labels = results.response.data.verticalDesignSpectrum.periods;
  verticalDesignSpectrumChartData.data = results.response.data.verticalDesignSpectrum.ordinates.map((point, index) => ({
    x: results.response.data.verticalDesignSpectrum.periods[index],
    y: point,
  }));
  load.details.push(verticalDesignSpectrumChartData);

  // SAMV Spectrum
  const savmSpectrumChartData = {};
  savmSpectrumChartData.type = 'chart';
  savmSpectrumChartData.title = 'MCER Vertical Response Spectrum';
  savmSpectrumChartData.labels = results.response.data.verticalMCErSpectrum.periods;
  savmSpectrumChartData.data = results.response.data.verticalMCErSpectrum.ordinates.map((point, index) => ({
    x: results.response.data.verticalMCErSpectrum.periods[index],
    y: point,
  }));
  load.details.push(savmSpectrumChartData);

  load.details.push({
    type: 'dataSource',
    dataSource: {
      text: standardsConfig[standardsVersion].dataSource,
    },
  });
  reportStore.emitChange();
};

const handle4123Results = (load, results) => {
  const { status, response } = results;

  if (status !== 'success' && status !== 'sucess') {
    throw new Error();
  }

  const { data } = response;

  load.details = [];
  load.sampleData.value = [];

  const resultsDefinition = [
    {
      hazardLevel: 'BSE-2N',
      items: [
        { label: 'sxs', value: data['sxsBSE2N'] },
        { label: 'sx1', value: data['sx1BSE2N'] },
      ],
      label: 'Hazard Level BSE-2N',
      type: 'grid',
    },
    {
      hazardLevel: 'BSE-2N',
      chartData: data['multiPeriodBSE2Nspectrum'],
      title: 'Horizontal Response Spectrum - Hazard Level BSE-2N',
      type: 'chart',
    },
    {
      hazardLevel: 'BSE-1N',
      items: [
        { label: 'sxs', value: data['sxsBSE1N'] },
        { label: 'sx1', value: data['sx1BSE1N'] },
      ],
      label: 'Hazard Level BSE-1N',
      type: 'grid',
    },
    {
      hazardLevel: 'BSE-1N',
      chartData: data['multiPeriodBSE1Nspectrum'],
      title: 'Horizontal Response Spectrum - Hazard Level BSE-1N',
      type: 'chart',
    },
    {
      hazardLevel: 'BSE-2E',
      items: [
        { label: 'sxs', value: data['sxsBSE2E'] },
        { label: 'sx1', value: data['sx1BSE2E'] },
      ],
      label: 'Hazard Level BSE-2E',
      type: 'grid',
    },
    {
      hazardLevel: 'BSE-2E',
      chartData: data['multiPeriodBSE2Espectrum'],
      title: 'Horizontal Response Spectrum - Hazard Level BSE-2E',
      type: 'chart',
    },
    {
      hazardLevel: 'BSE-1E',
      items: [
        { label: 'sxs', value: data['sxsBSE1E'] },
        { label: 'sx1', value: data['sx1BSE1E'] },
      ],
      label: 'Hazard Level BSE-1E',
      type: 'grid',
    },
    {
      hazardLevel: 'BSE-1E',
      chartData: data['multiPeriodBSE1Espectrum'],
      title: 'Horizontal Response Spectrum - Hazard Level BSE-1E',
      type: 'chart',
    },
  ];

  resultsDefinition.forEach((resultDef) => {
    if (resultDef.type === 'grid') {
      load.details.push({
        type: 'grid',
        label: resultDef.label,
        items: resultDef.items.map(({ label, value }) => ({
          label,
          value,
        })),
      });
    } else if (resultDef.type === 'chart') {
      const { chartData, title } = resultDef;

      load.details.push({
        type: 'chart',
        title,
        labels: chartData.periods,
        data: chartData.periods.map((period, index) => ({ x: period, y: chartData.ordinates[index] })),
      });
    }
  });

  reportStore.emitChange();
};

const handle4117Results = (load, results) => {
  if (results.request.status !== 'success') {
    throw new Error();
  }

  load.details = [];
  load.sampleData.value = [];

  const resultsDefinition = [
    {
      hazardLevel: 'BSE-2N',
      items: ['ssuh', 'crs', 'ssrt', 'ssd', 'ss', 'fa', 'sxs', 's1uh', 'cr1', 's1rt', 's1d', 's1', 'fv', 'sx1'],
      label: 'Hazard Level BSE-2N',
      type: 'grid',
    },
    {
      hazardLevel: 'BSE-2N',
      chartData: 'horizontalSpectrum',
      title: 'Horizontal Response Spectrum - Hazard Level BSE-2N',
      type: 'chart',
    },
    {
      hazardLevel: 'BSE-1N',
      items: ['sxs', 'sx1'],
      label: 'Hazard Level BSE-1N',
      type: 'grid',
    },
    {
      hazardLevel: 'BSE-2E',
      items: ['ss', 'fa', 'sxs', 's1', 'fv', 'sx1'],
      label: 'Hazard Level BSE-2E',
      type: 'grid',
    },
    {
      hazardLevel: 'BSE-1E',
      items: ['ss', 'fa', 'sxs', 's1', 'fv', 'sx1'],
      label: 'Hazard Level BSE-1E',
      type: 'grid',
    },
    {
      hazardLevel: 'TL Data',
      items: ['tl'],
      label: 'TL Data',
      type: 'grid',
    },
  ];

  const { data } = results.response;

  if (Array.isArray(data)) {
    resultsDefinition.forEach((resultDef) => {
      const hazardResult = data.find((dataResult) => dataResult.hazardLevel === resultDef.hazardLevel);

      if (hazardResult) {
        if (resultDef.type === 'grid') {
          const items = resultDef.items.map((item) => ({
            label: item,
            value: hazardResult[item],
          }));

          load.details.push({
            type: 'grid',
            label: resultDef.label,
            items,
          });
        } else if (resultDef.type === 'chart') {
          const chartData = hazardResult[resultDef.chartData];
          const values = [];
          const labels = [];

          chartData.forEach(([label, value]) => {
            labels.push(label);
            values.push({ x: label, y: value });
          });

          load.details.push({
            type: 'chart',
            title: resultDef.title,
            labels,
            data: values,
          });
        }
      }
    });

    reportStore.emitChange();
  } else {
    throw new Error();
  }
};

const querySeismic = (location, load) => {
  const standardsVersion = criteriaStore.getState().standardsVersion;
  const riskLevel = criteriaStore.getState().riskLevel;

  var riskLevelValue = 1;
  if (riskLevel === 3) {
    riskLevelValue = 1.25;
  }
  if (riskLevel === 4) {
    riskLevelValue = 1.5;
  }

  const soilClass = criteriaStore.getState().soilClass.toString();
  const url = standardsConfig[standardsVersion].seismicQueryUrl;
  const format = 'text';
  const useProxy = true;
  const usePost = false;
  const roman = romanize(riskLevel);
  const soilClassValue = standardsConfig[standardsVersion].soilClasses[soilClass];
  let letter = soilClassValue.split(' ')[0]; // Letter is always first index when split by space
  if (standardsVersion === '7-16' && soilClassValue === standardsConfig[standardsVersion].soilClasses[2]) {
    letter = 'B-estimated';
  }
  if (standardsVersion === '7-16' && soilClassValue === standardsConfig[standardsVersion].soilClasses[5]) {
    letter = 'D-default';
  }

  let riskCategoryLabel = '';

  const params = {};
  params.latitude = location.lat;
  params.longitude = location.long;
  params.referenceDocument = `ASCE${standardsVersion}`;
  if (standardsVersion !== '41-17' && standardsVersion !== '41-23') {
    params.riskCategory = roman;
    riskCategoryLabel = 'Risk Category ' + romanize(riskLevel);
  }
  params.siteClass = letter;
  params.title = 'ASCE';

  return new bbPromise((resolve, reject) => {
    try {
      if (standardsVersion === '7-16' && soilClass === '7') {
        load.sampleData.value = [riskCategoryLabel];
        load.details = [
          {
            type: 'singleLine',
            label: riskCategoryLabel,
            value:
              'A site response analysis shall be performed in accordance with ASCE/SEI 7-16 Section 21.1 for structures on Site Class F sites. If your structure is exempted under ASCE/SEI 7-16 Section 20.3.1, select a substitute site class.',
          },
        ];
        reportStore.emitChange();
      } else if (standardsVersion === '7-10' && soilClass === '5') {
        load.sampleData.value = [riskCategoryLabel];
        load.details = [
          {
            type: 'singleLine',
            label: riskCategoryLabel,
            value:
              'A site response analysis shall be performed in accordance with ASCE/SEI 7-10 Section 21.1 for structures on Site Class F sites. If your structure is exempted under ASCE/SEI 7-10 Section 20.3.1, select a substitute site class.',
          },
        ];
        reportStore.emitChange();
      } else {
        let request = esriRequest(
          {
            url: url,
            content: params,
            handleAs: format,
            callbackParamName: 'callback',
          },
          {
            usePost: usePost,
            useProxy: useProxy,
            disableIdentityLookup: false,
          }
        );

        request
          .then((response) => {
            const results = JSON.parse(response);
            switch (standardsVersion) {
              case '7-10':
                handle710Results(standardsVersion, load, riskLevelValue, results);
                break;
              case '7-16':
                handle716Results(standardsVersion, load, riskLevelValue, results);
                break;
              case '7-22':
                handle722Results(standardsVersion, load, results);
                break;
              case '41-17':
                handle4117Results(load, results);
                break;
              case '41-23':
                handle4123Results(load, results);
                break;
              default:
                throw new Error();
            }
            load.showInReport = true;
            resolve({ load: load.label });
          })
          .catch(() => {
            load.sampleData.value = ['Not available with criteria selected.'];
            reportStore.emitChange();
          });
      }
    } catch (error) {
      reject({ load: load.label, error });
    }
  });
};

export default querySeismic;
